<template>
  <div class="report-shell">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <v-container fluid class="pa-5">
      <!-- ============ hero ============ -->
      <div class="report-hero">
        <span class="hero-bar"></span>
        <div class="hero-left">
          <div class="hero-badge"><v-icon size="27">mdi-snake</v-icon></div>
          <div class="hero-copy">
            <div class="hero-eyebrow">
              <v-icon size="13">mdi-file-document-outline</v-icon>
              Snake Rescue Report
            </div>
            <div class="hero-title">{{ heroTitle }}</div>
            <div class="hero-meta">
              <span class="meta-chip" v-for="(chip, i) in heroMeta" :key="i">
                <v-icon size="13">{{ chip.icon }}</v-icon>
                {{ chip.text }}
              </span>
            </div>
          </div>
        </div>
        <div class="hero-right">
          <span :class="['status-chip', statusClass(data && data.status)]">
            <span class="status-dot"></span>
            {{ (data && data.status) || "—" }}
          </span>
        </div>
      </div>

      <!-- ============ response analytics (shared component, wrapped to match) ============ -->
      <div class="analytics-wrap" v-if="data && data.status">
        <ResponseTimesCard :data="data" />
      </div>

      <!-- ============ report details ============ -->
      <div class="panel" v-if="reportFields.length > 0">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-clipboard-text-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Report Details</div>
            <div class="subtext">What the public reported and who reported it</div>
          </div>
        </div>
        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in reportFields" :key="i">
            <div class="detail-cell">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value }}</div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- ============ rescue details ============ -->
      <div class="panel" v-if="showRescue && rescueFields.length > 0">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-hand-heart-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Rescue Details</div>
            <div class="subtext">Who carried out the rescue, and what they found</div>
          </div>
        </div>
        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in rescueFields" :key="i">
            <div class="detail-cell">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value }}</div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- ============ release details ============ -->
      <div class="panel" v-if="showRelease && releaseFields.length > 0">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-tree-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Release Details</div>
            <div class="subtext">Where the snake was returned to the wild</div>
          </div>
        </div>
        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in releaseFields" :key="i">
            <div class="detail-cell">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">
                  <span v-if="field.chip" :class="['value-chip', field.chip]">{{ field.value }}</span>
                  <span v-else>{{ field.value }}</span>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- ============ photo galleries ============ -->
      <div class="panel" v-for="group in imageGroups" :key="group.title">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">{{ group.icon }}</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">{{ group.title }}</div>
            <div class="subtext">
              {{ group.images.length }}
              {{ group.images.length === 1 ? "photograph" : "photographs" }} &middot; click to enlarge
            </div>
          </div>
        </div>
        <!-- one viewer per group, so the arrows step through that group's photos -->
        <div v-viewer="{ movable: false }">
          <v-layout wrap justify-start>
            <v-flex xs6 sm4 md3 lg2 pa-2 v-for="(item, index) in group.images" :key="index">
              <div class="thumb">
                <img :src="mediaURL + item" class="clickable" />
              </div>
            </v-flex>
          </v-layout>
        </div>
      </div>

      <!-- ============ location ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-map-marker-radius-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Location</div>
            <div class="subtext" v-if="mapPoints.length > 0">
              {{ mapPointsLabel }} &middot; tap a pin for detail
            </div>
            <div class="subtext" v-else>No coordinates were recorded for this report</div>
          </div>
        </div>
        <!-- v-show, not v-if: Maps needs the element in the DOM when initMap runs -->
        <div id="map" v-show="mapPoints.length > 0"></div>
        <div class="empty-state" v-if="mapPoints.length === 0">
          <v-icon size="30">mdi-map-marker-off-outline</v-icon>
          <div class="nodata pt-2">
            This report carries no reported, rescued or released coordinates.
          </div>
        </div>
      </div>

      <!-- ============ admin actions ============ -->
      <div class="panel action-panel" v-if="mainRole === 'admin' && data && data.status">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-shield-check-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Administrative Actions</div>
            <div class="subtext">Available for a report at status &ldquo;{{ data.status }}&rdquo;</div>
          </div>
        </div>
        <div class="action-row">
          <v-btn
            depressed
            class="act act--approve"
            v-if="data.status === 'Released'"
            @click="approveoperationDialog = true"
          >
            <v-icon small left>mdi-check-circle-outline</v-icon>
            <span class="btnlabel">Approve Operation</span>
          </v-btn>
          <v-btn
            depressed
            class="act act--close"
            v-if="['Pending', 'Assigned', 'Rescued', 'Released'].includes(data.status)"
            @click="approveDialog = true"
          >
            <v-icon small left>mdi-close-circle-outline</v-icon>
            <span class="btnlabel">Close Operation</span>
          </v-btn>
          <v-btn
            depressed
            class="act act--delete"
            v-if="data.status !== 'Fraud'"
            @click="rejectDialog = true"
          >
            <v-icon small left>mdi-delete-outline</v-icon>
            <span class="btnlabel">Delete Operation</span>
          </v-btn>
        </div>
      </div>
    </v-container>
    <v-dialog v-model="approveDialog" max-width="500">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-close-circle-outline</v-icon>
          <span class="cardtitle">Close Operation</span>
        </div>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <v-layout wrap justify-start>
                <v-flex xs12>
                  <div class="itemKey pb-1">Remarks</div>
                  <v-text-field outlined dense v-model="closeremarks">
                  </v-text-field>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn depressed class="dlg-cancel" :ripple="false" @click="approveDialog = false">
            <span class="btnlabel">Cancel</span>
          </v-btn>
          <v-btn depressed class="dlg-confirm" :ripple="false" @click="closeoperation()">
            <span class="btnlabel">Close</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="approveoperationDialog" max-width="500">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-check-circle-outline</v-icon>
          <span class="cardtitle">Approve Operation</span>
        </div>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <v-layout wrap justify-start>
                <v-flex xs12>
                  <div class="itemKey pb-1">Remarks</div>
                  <v-text-field outlined dense v-model="approveremarks">
                  </v-text-field>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn depressed class="dlg-cancel" :ripple="false" @click="approveoperationDialog = false">
            <span class="btnlabel">Cancel</span>
          </v-btn>
          <v-btn depressed class="dlg-confirm" :ripple="false" @click="approveoperation()">
            <span class="btnlabel">Approve</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="rejectDialog" max-width="500">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-alert-circle-outline</v-icon>
          <span class="cardtitle">Confirmation</span>
        </div>
        <v-card-text>Are you sure you want to delete the operation?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn depressed class="dlg-cancel" :ripple="false" @click="rejectDialog = false">
            <span class="btnlabel">Cancel</span>
          </v-btn>
          <v-btn depressed class="dlg-confirm" :ripple="false" @click="deleteoperation()">
            <span class="btnlabel">Submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script
  src="https://maps.googleapis.com/maps/api/js?libraries=places&key=AIzaSyBpukNl1L1qGqEwad87A1-MkZj8H_bKDZM&libraries=visualization,drawing"></script>
<script>
  var marker;
  import axios from "axios";
  import ResponseTimesCard from "@/views/Analytics/responseTimesCard";
  export default {
    components:{
      ResponseTimesCard
    },
    data() {
      return {
        appLoading: false,
        ServerError: false,
        showSnackBar: false,
        timeout: 5000,
        msg: null,
        data: [],
        reportedimages: [],
        rescuedimages:[],
        releasedimages:[],
        map: "",
        mapData: {},
        location: [],
        approveDialog: false,
        closeremarks: "",
        rejectDialog: false,
        divisionname: "",
        photoArray: [],
        approveremarks:"",
        idByAdmin: "",
        distance: "",
        approveoperationDialog: false,
      };
    },
    beforeMount() {
      this.getData();
    },
    //   mounted() {
    //   this.$nextTick(() => {
    //     this.initMap();
    //   });
    // },
    computed: {
      mainRole() {
        return this.$store.state.userRole;
      },
      report() {
        return (this.data && this.data.reportId) || {};
      },
      rescue() {
        return (this.data && this.data.rescue) || {};
      },
      release() {
        return (this.data && this.data.release) || {};
      },
      heroTitle() {
        var snake = this.report.snake;
        if (snake && snake.name) return snake.name;
        return "Snake Rescue Report";
      },
      heroMeta() {
        var chips = [];
        if (this.report.createdAt) {
          chips.push({ icon: "mdi-calendar-clock", text: this.formatDate(this.report.createdAt) });
        }
        var district = this.rescue.district || this.release.district;
        if (district) chips.push({ icon: "mdi-map-marker-outline", text: district });
        if (this.divisionname) {
          chips.push({ icon: "mdi-forest", text: this.divisionname + " Division" });
        }
        if (this.report.user && this.report.user.name) {
          chips.push({ icon: "mdi-account-outline", text: this.report.user.name });
        }
        return chips;
      },
      //section visibility, kept identical to the old per-block v-if conditions
      showRescue() {
        return !!this.data && !["Pending", "Assigned"].includes(this.data.status);
      },
      showRelease() {
        var loc = this.data && this.data.release ? this.data.release.location : null;
        return !!(loc && loc.length > 0);
      },
      reportFields() {
        var r = this.report;
        return this.compact([
          { label: "Snake", value: r.snake && r.snake.name, icon: "mdi-snake" },
          { label: "Situation", value: r.situation, icon: "mdi-alert-outline" },
          { label: "Created At", value: r.createdAt && this.formatDate(r.createdAt), icon: "mdi-calendar-clock" },
          {
            label: "Reported By",
            value: r.user ? r.user.name + " - " + r.user.phone : null,
            icon: "mdi-account-outline",
          },
          { label: "Division Name", value: this.divisionname, icon: "mdi-forest" },
          { label: "Description", value: r.description, icon: "mdi-text-long" },
        ]);
      },
      rescueFields() {
        var d = this.data || {};
        var c = this.rescue;
        return this.compact([
          {
            label: "Rescuer",
            value: d.rescuer && d.rescuer.name ? d.rescuer.name + " - " + d.rescuer.phone : null,
            icon: "mdi-account-hard-hat",
          },
          { label: "Rescued At", value: c.rescuedAt && this.formatDate(c.rescuedAt), icon: "mdi-clock-outline" },
          { label: "Rescued from", value: c.locality, icon: "mdi-map-marker-outline" },
          { label: "District", value: c.district, icon: "mdi-map-outline" },
          { label: "Quantity", value: c.quantity, icon: "mdi-counter" },
          { label: "Sex", value: c.sex, icon: "mdi-gender-male-female" },
          { label: "Laid Eggs", value: this.yesNo(c.laidEggs), icon: "mdi-egg-outline" },
          { label: "Gravid", value: this.yesNo(c.gravid), icon: "mdi-egg-easter" },
          { label: "Rescue Difficulties", value: c.difficulties, icon: "mdi-alert-decagram-outline" },
          { label: "Delay Reasons", value: c.delayReasons, icon: "mdi-timer-sand" },
        ]);
      },
      releaseFields() {
        var e = this.release;
        var handover = null;
        if (e.handoverplatform) {
          handover =
            e.handoverplatform === "Others"
              ? e.handoversipName || "Others"
              : e.handoverplatform;
        }
        return this.compact([
          { label: "Released Date", value: e.releasedAt && this.formatDate(e.releasedAt), icon: "mdi-calendar-check" },
          //the old template rendered these two whenever `release` existed
          { label: "Released or Not?", value: e.releasedOrNot == true ? "Yes" : "No", icon: "mdi-check-decagram-outline" },
          { label: "Snake Mishaps", value: e.mishaps == true ? "No Changes" : "Yes", icon: "mdi-alert-outline" },
          { label: "Released from", value: e.locality, icon: "mdi-map-marker-outline" },
          { label: "District", value: e.district, icon: "mdi-map-outline" },
          { label: "Snake Health", value: e.snakeHealth, icon: "mdi-heart-pulse" },
          { label: "Witness Name", value: e.witnessName, icon: "mdi-account-eye-outline" },
          { label: "Witness Designation", value: e.witnessDesignation, icon: "mdi-badge-account-outline" },
          {
            label: "Death Reason",
            value: e.isDead ? e.isDeadreason || null : null,
            icon: "mdi-emoticon-dead-outline",
            chip: "value-chip--danger",
          },
          {
            label: "Handed Over To",
            value: handover,
            icon: "mdi-handshake-outline",
            chip: "value-chip--ok",
          },
        ]);
      },
      //every coordinate this report actually carries, in reporting order.
      //stored [lon, lat] server-side, so the pairs are read reversed.
      mapPoints() {
        var points = [];
        var add = function (loc, title, detail, icon) {
          if (loc && loc.length === 2 && loc[0] !== null && loc[1] !== null) {
            points.push({ lat: loc[1], lon: loc[0], title: title, detail: detail, icon: icon });
          }
        };
        add(this.location, "Reported Location", this.report.situation || null, null);
        add(
          this.rescue.location,
          "Rescued Location",
          this.rescue.locality || null,
          "https://maps.google.com/mapfiles/ms/icons/green-dot.png"
        );
        add(
          this.release.location,
          "Released Location",
          this.release.locality || null,
          "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
        );
        return points;
      },
      //built here rather than in the template: an HTML entity inside {{ }} would
      //render as literal text
      mapPointsLabel() {
        return this.mapPoints
          .map(function (p) {
            return p.title.replace(" Location", "");
          })
          .join(" · ");
      },
      imageGroups() {
        var groups = [
          { title: "Reported Images", icon: "mdi-camera-outline", images: this.reportedimages },
          { title: "Rescued Images", icon: "mdi-image-multiple-outline", images: this.rescuedimages },
          { title: "Released Images", icon: "mdi-image-check-outline", images: this.releasedimages },
        ];
        return groups.filter(function (g) {
          return g.images && g.images.length > 0;
        });
      },
    },
    methods: {
      /*
        A google.maps.Map constructed without a `center` paints blank - that was
        the bug here: the center was only applied afterwards, and only when the
        report itself carried coordinates. Reports whose location sits on the
        rescue or release record (or nowhere) rendered an empty box.

        Now: collect whatever points exist, bail out early if there are none so the
        panel can show a message instead of a blank map, center on the first point,
        and fit the viewport around all of them when there is more than one.
      */
      initMap() {
        var vm = this;
        var maps = window.google && window.google.maps ? window.google.maps : null;
        if (!maps) return;

        var mapCanvas = document.getElementById("map");
        if (!mapCanvas) return;

        var points = vm.mapPoints;
        if (points.length === 0) return;

        vm.map = new maps.Map(mapCanvas, {
          zoom: 12,
          center: new maps.LatLng(points[0].lat, points[0].lon),
          mapTypeId: maps.MapTypeId.TERRAIN,
        });

        var bounds = new maps.LatLngBounds();

        points.forEach(function (point) {
          var position = new maps.LatLng(point.lat, point.lon);
          bounds.extend(position);

          var marker = new maps.Marker({
            map: vm.map,
            position: position,
            title: point.title,
            icon: point.icon,
          });

          var info = new maps.InfoWindow({
            content:
              '<div style="padding:4px 6px;font-family:poppinsregular,sans-serif">' +
              '<b style="font-size:13px">' + point.title + "</b>" +
              (point.detail
                ? '<div style="font-size:11.5px;color:#64748b;padding-top:2px">' +
                  point.detail +
                  "</div>"
                : "") +
              "</div>",
          });

          marker.addListener("click", function () {
            info.open(vm.map, marker);
          });
        });

        //show every pin at once rather than guessing a zoom that may exclude some
        if (points.length > 1) {
          vm.map.fitBounds(bounds);
        }

        //the panel lays out after the map is built, so nudge Maps to repaint
        //against the container's final size
        vm.$nextTick(function () {
          maps.event.trigger(vm.map, "resize");
          if (points.length === 1) {
            vm.map.setCenter(new maps.LatLng(points[0].lat, points[0].lon));
          }
        });
      },
      getData(successMsg) {
        this.appLoading = true;
        axios({
          method: "GET",
          url: "/operation/get",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          params: {
            id: this.$route.query.id,
          },
        })
          .then((response) => {
            if (response.data.status == true) {
              {
                this.data = response.data.data;
                if(this.data && this.data.division){
                this.divisionname = this.data.division[0].divisionName;
                }
                this.reportedimages = response.data.reportedimages;
                this.rescuedimages = response.data.rescuedimages;
                this.releasedimages = response.data.releasedimages;
                this.location = this.data.reportId.location;
                this.photoArray = this.data.photographs.map(
                  (photoObj) => photoObj.photo
                );
                this.appLoading = false;
                if (successMsg) {
                  this.msg = successMsg;
                  this.showSnackBar = true;
                }
                // this.msg = response.data.msg;
                // this.showSnackBar = true;
                //the response can land before this component mounts, so wait for
                //the DOM or getElementById("map") comes back null
                this.$nextTick(this.initMap);
              }
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.appLoading = false;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            (this.ServerError = true), console.log(err);
          });
      },
      closeoperation() {
        if (!this.closeremarks) {
          this.msg = "Please enter remarks.";
          this.showSnackBar = true;
          return;
        }
        this.appLoading = true;
        axios({
          method: "POST",
          url: "/operation/closereport",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          data: {
            id: this.$route.query.id,
            remarks: this.closeremarks,
          },
        })
          .then((response) => {
            if (response.data.status == true) {
              {
                this.approveDialog = false;
                this.getData("Report Closed Successfully.");
              }
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.appLoading = false;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
      
      approveoperation() {
        if (!this.approveremarks) {
          this.msg = "Please enter remarks.";
          this.showSnackBar = true;
          return;
        }
        this.appLoading = true;
        axios({
          method: "POST",
          url: "/operation/approve",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          data: {
            id: this.$route.query.id,
            remarks: this.approveremarks,
            idByAdmin: this.idByAdmin,
            distance:this.distance,
          },
        })
          .then((response) => {
            if (response.data.status == true) {
              {
                this.approveoperationDialog = false;
                this.getData("Approved Successfully.");
              }
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.appLoading = false;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
      deleteoperation() {
        this.appLoading = true;
        axios({
          method: "POST",
          url: "/operation/delete",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          data: {
            id: this.$route.query.id,
          },
        })
          .then((response) => {
            if (response.data.status == true) {
              {
                this.msg = "Operation Deleted Successfully.";
                this.showSnackBar = true;
                this.appLoading = false;
                this.rejectDialog = false;
                //this.$router.push({ path: "/approvedoperations" });
                setTimeout(() => {
          this.$router.push({ name: "snakereports" });
        }, 2000);
              }
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.appLoading = false;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
      formatDate(date) {
    if (!date) return '-';
    const [year, month, day] = date.slice(0, 10).split('-');
    return `${day}-${month}-${year}`;
  },
      //a field with nothing in it used to render an empty row; now it is dropped
      compact(fields) {
        return fields.filter(function (f) {
          return f.value !== null && f.value !== undefined && f.value !== "";
        });
      },
      yesNo(flag) {
        if (flag === true) return "Yes";
        if (flag === false) return "No";
        return null;
      },
      statusClass(status) {
        if (status === "Approved") return "status-approved";
        if (status === "Released") return "status-released";
        if (status === "Rescued") return "status-rescued";
        if (status === "Assigned") return "status-assigned";
        if (status === "Fraud") return "status-fraud";
        if (status === "Closed") return "status-closed";
        return "status-pending";
      }
    },
  };
  </script>
<style scoped>
/* ================= design tokens =================
   Same emerald system as the dashboards' stat cards and the snakebite alert view,
   so a report reads as part of the same product.
   NOTE: this block used to be an unscoped <style>, which leaked .heading,
   .subheading and an !important #map height into every other page. */
.report-shell {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);

  background: #f6f8fb;
  min-height: 100vh;
  padding-bottom: 2rem;
}

/* ================= typography ================= */
.cardtitle {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.subtext {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.45;
}
.itemKey {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5;
}
.itemValue {
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1b2a17;
  line-height: 1.4;
  word-break: break-word;
}
.btnlabel {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

/* ================= hero ================= */
.report-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 26px 22px 30px;
  border: 1px solid var(--tint-line);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, var(--tint) 100%);
  box-shadow: var(--shadow);
}
.hero-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, var(--accent) 0%, var(--ink) 100%);
}
.hero-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
  flex: 1 1 340px;
}
.hero-badge {
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--accent) 0%, var(--ink) 100%);
  box-shadow: 0 6px 18px -6px rgba(5, 150, 105, 0.55);
}
.hero-badge .v-icon {
  color: #ffffff !important;
}
.hero-copy {
  min-width: 0;
}
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--ink);
}
.hero-eyebrow .v-icon {
  color: var(--accent) !important;
}
.hero-title {
  margin-top: 2px;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #14251c;
  word-break: break-word;
}
.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border: 1px solid var(--tint-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.75);
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: #45564d;
  white-space: nowrap;
}
.meta-chip .v-icon {
  color: var(--accent) !important;
}
.hero-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  flex: 0 0 auto;
}

/* ================= status chip ================= */
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 16px;
  padding: 5px 14px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.status-pending {
  background: #fdecea;
  color: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.08);
}
.status-assigned {
  background: #fff4e0;
  color: #b26a00;
  box-shadow: 0 0 0 3px rgba(178, 106, 0, 0.08);
}
.status-rescued {
  background: #e8f1fd;
  color: #1565c0;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.08);
}
.status-released {
  background: #e0f2f1;
  color: #00695c;
  box-shadow: 0 0 0 3px rgba(0, 105, 92, 0.08);
}
.status-approved {
  background: #e8f5e9;
  color: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.08);
}
.status-closed {
  background: #eceff1;
  color: #455a64;
  box-shadow: 0 0 0 3px rgba(69, 90, 100, 0.08);
}
.status-fraud {
  background: #f3e8fa;
  color: #6a1b9a;
  box-shadow: 0 0 0 3px rgba(106, 27, 154, 0.08);
}

/* ================= panels ================= */
.panel {
  margin-top: 20px;
  padding: 22px 24px 24px 24px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--line);
}
.panel-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.panel-icon .v-icon {
  color: var(--ink) !important;
}
.panel-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

/* the shared ResponseTimesCard is used by four pages, so it is restyled from
   here rather than edited - only this page's instance changes */
.analytics-wrap {
  margin-top: 20px;
}
.analytics-wrap ::v-deep .v-card {
  border: 1px solid var(--line) !important;
  border-radius: 18px !important;
  background: #ffffff !important;
  box-shadow: var(--shadow) !important;
  padding: 22px 24px !important;
}
.analytics-wrap ::v-deep .flex {
  margin-bottom: 0;
}
.analytics-wrap ::v-deep .caption {
  font-family: poppinsmedium, poppinsregular, sans-serif !important;
  font-size: 10.5px !important;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5 !important;
}

/* ================= detail grid ================= */
.detail-cell {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  height: 100%;
  padding: 12px 14px;
  border: 1px solid #edf2f6;
  border-radius: 12px;
  background: #fbfdfe;
  transition: border-color 0.25s ease, background 0.25s ease;
}
.detail-cell:hover {
  border-color: var(--tint-line);
  background: var(--tint);
}
.detail-icon {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.detail-icon .v-icon {
  color: var(--ink) !important;
}
.detail-copy {
  min-width: 0;
}
.detail-copy .itemValue {
  margin-top: 3px;
}
.value-chip {
  display: inline-block;
  border-radius: 12px;
  padding: 2px 10px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11px;
  font-weight: 600;
}
.value-chip--danger {
  background: #fdecea;
  color: #c62828;
}
.value-chip--ok {
  background: var(--badge);
  color: var(--ink);
}

/* ================= galleries ================= */
.thumb {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #f2f6f8;
  aspect-ratio: 4 / 3;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.thumb:hover {
  border-color: var(--accent);
  box-shadow: 0 12px 26px -14px rgba(5, 150, 105, 0.45);
}
.thumb:hover img {
  transform: scale(1.06);
}
.clickable {
  cursor: pointer;
}
/* aspect-ratio fallback for older WebKit */
@supports not (aspect-ratio: 4 / 3) {
  .thumb {
    height: 150px;
  }
}

/* ================= empty states ================= */
.empty-state {
  padding: 26px 0 18px 0;
  text-align: center;
}
.empty-state .v-icon {
  color: #c3d2dc !important;
}
.nodata {
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  color: #8a998a;
}

/* ================= map ================= */
#map {
  height: 380px;
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
}

/* ================= admin actions ================= */
.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 16px;
}
.act.v-btn {
  height: 42px !important;
  padding: 0 20px !important;
  border-radius: 22px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: filter 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
.act.v-btn,
.act.v-btn .btnlabel,
.act.v-btn .v-icon {
  color: #ffffff !important;
}
.act.v-btn:hover {
  filter: brightness(1.07);
  transform: translateY(-1px);
}
.act--approve.v-btn {
  background: linear-gradient(135deg, #10b981 0%, #047857 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(5, 150, 105, 0.45) !important;
}
.act--close.v-btn {
  background: linear-gradient(135deg, #f0a132 0%, #b26a00 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(178, 106, 0, 0.45) !important;
}
.act--delete.v-btn {
  background: linear-gradient(135deg, #ef5350 0%, #c62828 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(198, 40, 40, 0.45) !important;
}

/* ================= dialogs ================= */
/*
 * v-dialog detaches its content to the app root, so the dialog is NOT a
 * descendant of .report-shell and the tokens declared there never reach it.
 * Custom properties inherit down the DOM tree, and once var(--accent) resolves
 * to nothing the whole declaration is invalid at computed-value time: the
 * confirm button lost its background entirely and, still carrying
 * `color: #ffffff`, rendered as a white label on a white card. Declare what the
 * dialog needs on the dialog's own elements.
 */
.dialog-title,
.dlg-cancel.v-btn,
.dlg-confirm.v-btn {
  --accent: #10b981;
  --ink: #047857;
  --line: #e7eef3;
}
.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px 14px 22px;
  border-bottom: 1px solid var(--line);
}
.dialog-title .v-icon {
  color: var(--ink) !important;
}
.dlg-cancel.v-btn {
  border-radius: 20px !important;
  border: 1px solid #f3c9c9 !important;
  background: #ffffff !important;
  box-shadow: none !important;
  text-transform: none !important;
  letter-spacing: normal !important;
}
.dlg-cancel.v-btn,
.dlg-cancel.v-btn .btnlabel {
  color: #c62828 !important;
}
.dlg-confirm.v-btn {
  border-radius: 20px !important;
  background: linear-gradient(135deg, var(--accent) 0%, var(--ink) 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(5, 150, 105, 0.45) !important;
  text-transform: none !important;
  letter-spacing: normal !important;
}
.dlg-confirm.v-btn,
.dlg-confirm.v-btn .btnlabel {
  color: #ffffff !important;
}

/* ================= responsive ================= */
@media (max-width: 700px) {
  .report-hero {
    padding: 18px 18px 18px 22px;
  }
  .hero-right {
    align-items: flex-start;
    width: 100%;
  }
  .hero-title {
    font-size: 21px;
  }
  .panel {
    padding: 18px 16px 20px 16px;
  }
  .analytics-wrap ::v-deep .v-card {
    padding: 18px 16px !important;
  }
  .act.v-btn {
    width: 100%;
  }
  #map {
    height: 280px;
  }
}
</style>

<!-- .heading and .subheading stay UNSCOPED on purpose: 29 other views use these
     class names without defining them and rely on a global definition leaking
     from a page like this one. Values are the originals, byte for byte.
     Everything else this page needs now lives in the scoped block above. -->
<style>
.heading {
  font-family: poppinsregular;
  font-size: 20px;
  font-weight: 900;
}

.subheading {
  font-family: poppinsregular;
  font-size: 15px;
  font-weight: 500;
}
</style>
